import { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiChatProcessingOutline } from "@mdi/js";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  FabProps,
  TextField,
} from "@mui/material";

import CircularLoader from "components/CircularLoader";
import RadioGroupRating from "../RadioGroupRating";

const DETAILS_LIMIT = 500;
export interface OnSendProps {
  details: string;
  userSatisfaction: number;
}

export interface FeedbackDialogProps {
  fabSx?: FabProps["sx"];
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  onSend: ({ details, userSatisfaction }: OnSendProps) => Promise<void>;
}

function FeedbackDialog(props: FeedbackDialogProps) {
  const { fabSx, open, onClose, onOpen, onSend } = props;
  const [loading, setLoading] = useState(false);
  const [userSatisfaction, setUserSatisfaction] = useState(3);
  const [details, setDetails] = useState("");

  const handleUserSatisfaction = (e: React.SyntheticEvent<Element, Event>, value: number | null) => {
    if (value) {
      setUserSatisfaction(value);
    }
  };

  const handleDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= DETAILS_LIMIT) setDetails(e.target.value);
  };

  const handleSend = async () => {
    setLoading(true);
    await onSend({ details, userSatisfaction });
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      setDetails("");
      setUserSatisfaction(3);
    }
  }, [open]);

  return (
    <div>
      {!open && (
        <Fab
          aria-label='Open feedback'
          sx={{
            transform: "translate3d(0, 0, 0)",
            zIndex: "fab",
            ...fabSx,
          }}
          color='secondary'
          onClick={onOpen}
          size='small'
        >
          <Icon path={mdiChatProcessingOutline} size={1} />
        </Fab>
      )}
      <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
        <DialogTitle>Send feedback</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send us any ideas, encountered bugs or just an appreciation thought. Your feedback is much appreciated!
          </DialogContentText>
          <Box pt={3} pb={2}>
            <TextField
              autoFocus
              fullWidth
              helperText={`${details.length}/${DETAILS_LIMIT}`}
              id='details'
              label='Feedback details'
              maxRows={6}
              minRows={3}
              multiline
              onChange={handleDetails}
              value={details}
            />
          </Box>
          <RadioGroupRating
            defaultValue={userSatisfaction}
            highlightSelectedOnly
            label='How would you rate your experience ?'
            name='User satisfaction'
            onChange={handleUserSatisfaction}
            value={userSatisfaction}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <CircularLoader loading={loading}>
            <Button disabled={loading} onClick={handleSend}>
              Send
            </Button>
          </CircularLoader>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FeedbackDialog;
